import { component } from 'picoapp'
import $ from 'quantdom'

/**
 * Handle video click - show modal on desktop, inline video on mobile
 * @param {string} videoId - The video ID
 * @param {Event} event - The click event
 */
function handleVideoClick(videoId, event) {
  event.preventDefault();
  
  // Check if device is mobile using window width (matches theme's mobile breakpoint)
  const isMobile = window.innerWidth < 768;
  
  if (isMobile) {
    // Mobile: Show inline video
    const clickedContainer = event.currentTarget;
    const thumbnail = clickedContainer.querySelector('img');
    const playButton = clickedContainer.querySelector('[data-play-button]');
    const inlineVideo = clickedContainer.querySelector('.mobile-inline-video');
    const video = inlineVideo ? inlineVideo.querySelector('video') : null;

    if (thumbnail && playButton && inlineVideo && video) {
      // Hide thumbnail and play button
      thumbnail.style.display = 'none';
      playButton.style.display = 'none';

      // Show inline video
      inlineVideo.classList.remove('tw-hidden');

      // Load video sources if not already loaded (using the lazy loading pattern from video-element.liquid)
      if (!video.hasAttribute('data-sources-loaded')) {
        const sources = video.getAttribute('data-sources');
        if (sources) {
          try {
            const sourceList = JSON.parse(sources);
            // Clear existing sources first
            video.innerHTML = '';
            sourceList.forEach(source => {
              const sourceEl = document.createElement('source');
              sourceEl.src = source.url;
              sourceEl.type = source.mime_type;
              video.appendChild(sourceEl);
            });
            video.setAttribute('data-sources-loaded', 'true');
            video.load();
          } catch (e) {
            console.warn('Failed to parse video sources:', e);
          }
        }
      }

      // Clean up any existing event listeners first
      const existingResetHandler = video._resetVideoHandler;
      const existingFullscreenHandler = video._fullscreenHandler;
      
      if (existingResetHandler) {
        video.removeEventListener('ended', existingResetHandler);
      }
      
      if (existingFullscreenHandler) {
        video.removeEventListener('play', existingFullscreenHandler);
      }

      // Create reset function and store reference on the video element
      const resetVideo = () => {
        if (video) {
          video.pause();
          video.muted = true;
        }
        if (thumbnail) thumbnail.style.display = '';
        if (playButton) playButton.style.display = '';
        if (inlineVideo) inlineVideo.classList.add('tw-hidden');

        // Clean up event listeners
        video.removeEventListener('ended', resetVideo);
        if (video._fullscreenHandler) {
          video.removeEventListener('play', video._fullscreenHandler);
          delete video._fullscreenHandler;
        }
        if (video._fullscreenChangeHandler) {
          document.removeEventListener('fullscreenchange', video._fullscreenChangeHandler);
          document.removeEventListener('webkitfullscreenchange', video._fullscreenChangeHandler);
          document.removeEventListener('mozfullscreenchange', video._fullscreenChangeHandler);
          document.removeEventListener('MSFullscreenChange', video._fullscreenChangeHandler);
          delete video._fullscreenChangeHandler;
        }
        // Clean up iOS-specific listeners
        if (video._iosFullscreenHandler) {
          video.removeEventListener('play', video._iosFullscreenHandler);
          delete video._iosFullscreenHandler;
        }
        if (video._iosExitHandler) {
          video.removeEventListener('webkitendfullscreen', video._iosExitHandler);
          delete video._iosExitHandler;
        }
        if (video._escapeHandler) {
          document.removeEventListener('keydown', video._escapeHandler);
          delete video._escapeHandler;
        }
        delete video._resetVideoHandler;
      };

      // Create function to show thumbnail and reset video (for escape key and fullscreen exit)
      const showThumbnail = () => {
        console.log('showThumbnail function called');
        if (video) {
          video.pause();
          video.muted = true;
          video.currentTime = 0; // Reset to beginning
        }
        if (thumbnail) {
          thumbnail.style.display = '';
          console.log('Thumbnail shown');
        }
        if (playButton) {
          playButton.style.display = '';
          console.log('Play button shown');
        }
        if (inlineVideo) {
          inlineVideo.classList.add('tw-hidden');
          console.log('Inline video hidden');
        }
      };

      // Handle escape key press on mobile
      const handleEscapeKey = (e) => {
        if (e.key === 'Escape') {
          console.log('Escape key pressed on mobile video');
          showThumbnail();
          // Clean up the escape key listener after use
          document.removeEventListener('keydown', handleEscapeKey);
          delete video._escapeHandler;
        }
      };

      // Store the function references for cleanup
      video._resetVideoHandler = resetVideo;
      video._escapeHandler = handleEscapeKey;

      // Add escape key listener
      document.addEventListener('keydown', handleEscapeKey);

      // Unmute and play the video
      video.muted = false;
      
      // Check if device is mobile to force fullscreen (test on all mobile devices)
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isMobileDevice = isAndroid || isIOS || isMobile;
      
      console.log('Device detection:', { 
        isAndroid, 
        isIOS, 
        isMobile, 
        isMobileDevice, 
        userAgent: navigator.userAgent 
      });
      
      if (isMobileDevice) {
        if (isIOS) {
          // iOS-specific handling using webkitEnterFullscreen
          const enterFullscreenIOS = () => {
            console.log('Attempting to enter fullscreen on iOS');
            
            if (video.webkitEnterFullscreen) {
              console.log('Using webkitEnterFullscreen for iOS');
              try {
                video.webkitEnterFullscreen();
              } catch (err) {
                console.log('iOS fullscreen failed:', err);
              }
            } else {
              console.log('webkitEnterFullscreen not available on iOS');
            }
            
            video.removeEventListener('play', enterFullscreenIOS);
          };
          
          // iOS-specific fullscreen exit handling
          const handleIOSFullscreenExit = () => {
            console.log('iOS fullscreen ended');
            showThumbnail();
            // Clean up iOS-specific listeners
            video.removeEventListener('webkitendfullscreen', handleIOSFullscreenExit);
            if (video._iosFullscreenHandler) {
              delete video._iosFullscreenHandler;
            }
            if (video._iosExitHandler) {
              delete video._iosExitHandler;
            }
          };
          
          video.addEventListener('play', enterFullscreenIOS);
          video.addEventListener('webkitendfullscreen', handleIOSFullscreenExit);
          
          // Store the handlers for cleanup
          video._iosFullscreenHandler = enterFullscreenIOS;
          video._iosExitHandler = handleIOSFullscreenExit;
          
        } else {
          // Android and other mobile devices - use standard fullscreen API
          const enterFullscreen = () => {
            console.log('Attempting to enter fullscreen on Android/mobile');
            
            if (video.requestFullscreen) {
              console.log('Using requestFullscreen');
              video.requestFullscreen().then(() => {
                console.log('Fullscreen entered successfully');
              }).catch(err => {
                console.log('Fullscreen failed:', err);
              });
            } else if (video.webkitRequestFullscreen) {
              console.log('Using webkitRequestFullscreen');
              video.webkitRequestFullscreen();
            } else if (video.mozRequestFullScreen) {
              console.log('Using mozRequestFullScreen');
              video.mozRequestFullScreen();
            } else if (video.msRequestFullscreen) {
              console.log('Using msRequestFullscreen');
              video.msRequestFullscreen();
            } else {
              console.log('No fullscreen API available');
            }
            
            video.removeEventListener('play', enterFullscreen);
          };
          
          // Handle fullscreen change events (when user exits fullscreen)
          const handleFullscreenChange = () => {
            console.log('Fullscreen change event fired!');
            console.log('document.fullscreenElement:', document.fullscreenElement);
            console.log('document.webkitFullscreenElement:', document.webkitFullscreenElement);
            console.log('document.mozFullScreenElement:', document.mozFullScreenElement);
            console.log('document.msFullscreenElement:', document.msFullscreenElement);
            
            const isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || 
                                     document.mozFullScreenElement || document.msFullscreenElement);
            
            console.log('Is currently fullscreen:', isFullscreen);
            
            if (!isFullscreen) {
              showThumbnail();
              // Clean up fullscreen change listeners
              document.removeEventListener('fullscreenchange', handleFullscreenChange);
              document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
              document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
              document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
              if (video._fullscreenChangeHandler) {
                delete video._fullscreenChangeHandler;
              }
            }
          };

          video.addEventListener('play', enterFullscreen);

          // Add fullscreen change listeners for all browsers BEFORE entering fullscreen
          console.log('Adding fullscreen change listeners...');
          document.addEventListener('fullscreenchange', handleFullscreenChange);
          document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
          document.addEventListener('mozfullscreenchange', handleFullscreenChange);
          document.addEventListener('MSFullscreenChange', handleFullscreenChange);
          // Store the handlers for cleanup
          video._fullscreenHandler = enterFullscreen;
          video._fullscreenChangeHandler = handleFullscreenChange;
        }
      }
      video.play().catch(error => {
        console.warn('Video autoplay failed:', error);
        // If autoplay fails, keep it unmuted so user can manually play
      });

      // Add listener for when video ends
      video.addEventListener('ended', resetVideo);
    }
  } else {
    // Desktop: Show modal
    if (window.MicroBus) {
      window.MicroBus.emit('video-modal-' + videoId + ':modal:toggle');
    }
  }
}

// Make the function available globally so it can be called from the Liquid template
if (typeof window !== 'undefined') {
  window.handleVideoClick = handleVideoClick;
}

/**
 * Video Modal Handler Component
 *
 * Automatically handles video playback control for modal-item elements containing videos.
 * When a video modal is closed (via close button, ESC key, overlay click, or MicroBus events),
 * all videos within the modal are paused and reset to the beginning.
 *
 * Usage: Add `data-component="videoModalHandler"` to any modal-item that contains videos.
 * The component will automatically detect if it's a video modal and set up the necessary handlers.
 */
export default component((node) => {
  /**
   * Pause and reset video to beginning
   * @param {HTMLVideoElement} video - The video element to reset
   */
  const resetVideo = (video) => {
    if (video && video.tagName === 'VIDEO') {
      video.pause()
      video.currentTime = 0
      video.muted = true // Mute video when resetting
    }
  }

  /**
   * Unmute and play video
   * @param {HTMLVideoElement} video - The video element to unmute and play
   */
  const playVideo = (video) => {
    if (video && video.tagName === 'VIDEO') {
      video.muted = false
      video.play().catch((error) => {
        console.warn('Video autoplay failed:', error)
        // If autoplay fails, keep it unmuted so user can manually play
      })
    }
  }

  /**
   * Lazy load video sources when modal opens
   * @param {HTMLVideoElement} videoElement - The video element to load sources for
   */
  const loadVideoSources = (videoElement) => {
    if (videoElement && !videoElement.hasAttribute('data-sources-loaded')) {
      // Get the data attributes with video URLs
      const sources = JSON.parse(videoElement.getAttribute('data-sources') || '[]')
      
      if (sources.length > 0) {
        // Clear existing sources
        videoElement.innerHTML = ''
        
        // Add each source
        sources.forEach(source => {
          const sourceEl = document.createElement('source')
          sourceEl.src = source.url
          sourceEl.type = source.mime_type
          videoElement.appendChild(sourceEl)
        })
        
        // Mark as loaded and load the video
        videoElement.setAttribute('data-sources-loaded', 'true')
        videoElement.load()
      }
    }
  }

  /**
   * Find all videos within a container
   * @param {HTMLElement} container - Container to search for videos
   * @returns {Array} Array of video elements
   */
  const findVideosInContainer = (container) => {
    return Array.from(container.querySelectorAll('video'))
  }

  /**
   * Reset all videos in the current node (modal)
   */
  const resetAllVideos = () => {
    const videos = findVideosInContainer(node)
    videos.forEach(resetVideo)
  }

  /**
   * Play and unmute all videos in the current node (modal)
   */
  const playAllVideos = () => {
    const videos = findVideosInContainer(node)
    videos.forEach(playVideo)
  }

  /**
   * Handle modal close events
   */
  const handleModalClose = () => {
    // Small delay to ensure modal state has updated
    setTimeout(() => {
      const modal = node
      if (!modal.open || !modal.hasAttribute('open')) {
        resetAllVideos()
      }
    }, 50)
  }

  /**
   * Handle modal open events
   */
  const handleModalOpen = () => {
    // Small delay to ensure modal state has updated
    setTimeout(() => {
      const modal = node
      if (modal.open || modal.hasAttribute('open')) {
        // Load video sources first
        const videos = findVideosInContainer(node)
        videos.forEach(loadVideoSources)
        
        // Then play videos after a short delay to ensure sources are loaded
        setTimeout(() => {
          playAllVideos()
        }, 100)
      }
    }, 100)
  }

  // Get modal ID from the node
  const modalId = node.id

  // Only proceed if this is a video modal
  if (!modalId || !modalId.includes('video-modal')) {
    return
  }

  // Listen for MicroBus modal toggle events
  if (window.MicroBus) {
    window.MicroBus.on(`${modalId}:modal:toggle`, handleModalClose)
    window.MicroBus.on(`${modalId}:modal:open`, handleModalOpen)
  }

  // Set up mutation observer to watch for modal open/close state changes
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
        if (!node.hasAttribute('open')) {
          resetAllVideos()
        } else {
          handleModalOpen()
        }
      }
    })
  })

  observer.observe(node, { attributes: true })

  // Handle close button clicks
  const handleClose = (e) => {
    resetAllVideos()
  }
  $(node).on('click', '[slot="close"], [data-modal-close-btn]', handleClose)

  // Handle ESC key press
  const handleKeyPress = (e) => {
    if (e.key === 'Escape' && node.hasAttribute('open')) {
      resetAllVideos()
    }
  }

  // Handle overlay clicks (clicking on the modal backdrop)
  const handleOverlayClick = (e) => {
    if (e.target === node) {
      resetAllVideos()
    }
  }

  // Add event listeners
  document.addEventListener('keydown', handleKeyPress)
  $(node).on('click', handleOverlayClick)

  // Cleanup function
  return () => {
    observer.disconnect()
    document.removeEventListener('keydown', handleKeyPress)
    $(node).off('click', handleOverlayClick)
    $(node).off('click', '[slot="close"], [data-modal-close-btn]', handleClose)

    if (window.MicroBus) {
      window.MicroBus.off(`${modalId}:modal:toggle`, handleModalClose)
      window.MicroBus.off(`${modalId}:modal:open`, handleModalOpen)
    }
  }
})
